import { useEffect, useMemo, useState, type ReactNode } from "react";
import type {
  BrandingSettings,
  LocalLicenseStatus,
  NavKey,
  PanelUser,
  SessionInfo
} from "../types";
import {
  sectionsForRole,
  toolsForRole,
  type WhmSectionId,
  type WhmTool
} from "../whmTools";
import {
  CPANEL_SECTION_ICONS,
  CPANEL_SECTIONS,
  cpanelSectionDefaultAct,
  cpanelSectionTools,
  type CpanelSectionId,
  type CpanelTool
} from "../cpanelTools";
import { hrefForAct, hrefForNav } from "../lib/routes";

const SECTION_ICONS: Record<string, string> = {
  home: "⌂",
  accounts: "👤",
  plan: "📋",
  packages: "▦",
  plans: "📋",
  domains: "◎",
  ssl: "🔒",
  dns: "🌐",
  email: "✉",
  sql: "⛁",
  security: "⛨",
  backup: "⇩",
  import: "⇩",
  transfers: "⇄",
  server: "🖥",
  services: "⚙",
  apps: "📦",
  software: "◐",
  reseller: "👥",
  advanced: "◇"
};

const EXPAND_KEY = "realpanel_whm_nav_expanded_v1";

export type WhmNavOpts = {
  createAccount?: boolean;
  serverConfigTab?:
    | "status"
    | "basic"
    | "tweaks"
    | "php"
    | "metrics"
    | "panel";
  advancedTab?:
    | "overview"
    | "cluster"
    | "limits"
    | "integrations"
    | "licenses"
    | "appearance";
  toolId?: string;
  /** FTP page tab */
  ftpTab?: "manage" | "add" | "connections";
};

function loadExpanded(): string | null {
  try {
    return localStorage.getItem(EXPAND_KEY);
  } catch {
    return null;
  }
}

function saveExpanded(id: string | null) {
  try {
    if (id) localStorage.setItem(EXPAND_KEY, id);
    else localStorage.removeItem(EXPAND_KEY);
  } catch {
    /* */
  }
}

/**
 * Webuzo-style shell: expandable section menus with tool sub-items.
 */
export function Shell({
  user,
  session,
  nav,
  onNav,
  onLogout,
  onStopImpersonation,
  branding,
  licenseStatus,
  onOpenLicenses,
  whmSection,
  onWhmSection,
  userFeatures,
  os,
  meta,
  loadAvg,
  whmSearch,
  onWhmSearch,
  activeToolId: activeToolIdProp,
  authorityMode = "master",
  children
}: {
  user: PanelUser;
  session: SessionInfo | null;
  nav: NavKey;
  onNav: (k: NavKey, opts?: WhmNavOpts) => void;
  onLogout: () => void;
  onStopImpersonation: () => void;
  branding?: BrandingSettings | null;
  licenseStatus?: LocalLicenseStatus | null;
  onOpenLicenses?: () => void;
  whmSection?: WhmSectionId | "all";
  onWhmSection?: (s: WhmSectionId | "all") => void;
  userFeatures?: Record<string, boolean> | null;
  os?: Record<string, string> | null;
  meta?: { phase?: number; version?: string } | null;
  loadAvg?: string | null;
  whmSearch?: string;
  onWhmSearch?: (q: string) => void;
  activeToolId?: string | null;
  /** master = Owner Master Panel; customer = hosting-only node */
  authorityMode?: "master" | "customer";
  children: ReactNode;
}) {
  const staff = user.role === "superadmin" || user.role === "admin";
  const reseller = user.role === "reseller";
  const userPanel = (!staff && !reseller) || Boolean(session?.impersonating);
  const whmMode = !userPanel;

  const whmSections = sectionsForRole(user.role, authorityMode);
  const tools = useMemo(
    () => toolsForRole(user.role, authorityMode),
    [user.role, authorityMode]
  );

  const toolsBySection = useMemo(() => {
    const map = new Map<string, WhmTool[]>();
    for (const t of tools) {
      const arr = map.get(t.section) || [];
      arr.push(t);
      map.set(t.section, arr);
    }
    return map;
  }, [tools]);

  const [expanded, setExpanded] = useState<string | null>(() => {
    const saved = loadExpanded();
    if (saved && saved !== "home") return saved;
    return null;
  });
  const [localToolId, setLocalToolId] = useState<string | null>(null);
  const activeToolId = activeToolIdProp ?? localToolId;

  // Auto-expand section matching current nav (WHM + full cPanel sections)
  useEffect(() => {
    if (!whmMode) {
      // cPanel: expand the section for the active page
      const byNav: Partial<Record<NavKey, CpanelSectionId>> = {
        overview: "home",
        sites: "domains",
        ssl: "ssl",
        databases: "databases",
        files: "files",
        ftp: "ftp",
        apps: "software",
        email: "mail",
        cron: "advanced",
        security: "security",
        logs: "metrics",
        metrics: "metrics",
        polish: "preferences",
        backups: "files"
      };
      const sectionIds: CpanelSectionId[] = [
        "domains",
        "ssl",
        "databases",
        "files",
        "ftp",
        "software",
        "mail",
        "advanced",
        "security",
        "metrics",
        "preferences"
      ];
      const sec =
        (activeToolId &&
          sectionIds.find((id) =>
            cpanelSectionTools(id, userFeatures).some(
              (t) => t.id === activeToolId
            )
          )) ||
        byNav[nav];
      if (sec && sec !== "home") {
        setExpanded(sec);
        saveExpanded(sec);
      }
      return;
    }
    if (nav === "ssl") {
      setExpanded("ssl");
      saveExpanded("ssl");
      return;
    }
    if (nav === "ftp") {
      setExpanded("ftp");
      saveExpanded("ftp");
      return;
    }
    if (!whmMode) return;
    if (nav === "overview") {
      if (whmSection && whmSection !== "all" && whmSection !== "home") {
        setExpanded(whmSection);
        saveExpanded(whmSection);
      }
      return;
    }
    // Prefer the active tool's section (Plan vs Package both use nav "plans")
    if (activeToolId) {
      const tool = tools.find((t) => t.id === activeToolId);
      if (tool?.section && tool.section !== "home") {
        setExpanded(tool.section);
        saveExpanded(tool.section);
        return;
      }
    }
    const match = whmSections.find(
      (s) =>
        s.nav === nav ||
        (toolsBySection.get(s.id) || []).some((t) => t.nav === nav)
    );
    if (match && match.id !== "home") {
      setExpanded(match.id);
      saveExpanded(match.id);
    }
  }, [
    nav,
    whmMode,
    whmSection,
    whmSections,
    toolsBySection,
    activeToolId,
    tools
  ]);

  const productName = branding?.productName || "RealPanel";
  const panelLabel = userPanel
    ? "cPanel"
    : reseller
      ? "Reseller WHM"
      : "WHM";

  const prettyOs =
    os?.prettyName ||
    os?.platform ||
    (os?.id ? String(os.id) : null) ||
    "Linux";
  const version =
    meta?.version?.replace(/^0\.\d+\.\d+-?/, "") ||
    meta?.version ||
    (meta?.phase != null ? `W${meta.phase}` : "");
  const hostname = os?.hostname || "—";

  const openTool = (t: WhmTool, e?: { preventDefault: () => void }) => {
    e?.preventDefault();
    setLocalToolId(t.id);
    onWhmSection?.(t.section);
    setExpanded(t.section);
    saveExpanded(t.section);

    if (t.id === "create-account" || t.hint === "create") {
      onNav("accounts", { createAccount: true, toolId: t.id });
      return;
    }
    if (
      t.hint === "status" ||
      t.hint === "basic" ||
      t.hint === "tweaks" ||
      t.hint === "php" ||
      t.hint === "metrics"
    ) {
      onNav(t.nav, { serverConfigTab: t.hint, toolId: t.id });
      return;
    }
    if (
      t.hint === "overview" ||
      t.hint === "cluster" ||
      t.hint === "limits" ||
      t.hint === "integrations" ||
      t.hint === "licenses" ||
      t.hint === "appearance"
    ) {
      onNav(t.nav, { advancedTab: t.hint, toolId: t.id });
      return;
    }
    onNav(t.nav, { toolId: t.id });
  };

  const toolHref = (t: WhmTool): string => {
    if (t.id === "create-account" || t.hint === "create") {
      return hrefForNav("accounts", { createAccount: true, toolId: t.id });
    }
    if (
      t.hint === "status" ||
      t.hint === "basic" ||
      t.hint === "tweaks" ||
      t.hint === "php" ||
      t.hint === "metrics"
    ) {
      return hrefForNav(t.nav, {
        serverConfigTab: t.hint,
        toolId: t.id
      });
    }
    if (
      t.hint === "overview" ||
      t.hint === "cluster" ||
      t.hint === "limits" ||
      t.hint === "integrations" ||
      t.hint === "licenses" ||
      t.hint === "appearance"
    ) {
      return hrefForNav(t.nav, { advancedTab: t.hint, toolId: t.id });
    }
    return hrefForNav(t.nav, { toolId: t.id });
  };

  const openCpanelTool = (t: CpanelTool, e?: { preventDefault: () => void }) => {
    e?.preventDefault();
    setLocalToolId(t.id);
    setExpanded(t.section);
    saveExpanded(t.section);
    if (t.section === "ftp") {
      const ftpTab =
        t.hint === "add"
          ? "add"
          : t.hint === "connections"
            ? "connections"
            : "manage";
      onNav("ftp", { ftpTab, toolId: t.id });
      return;
    }
    if (t.id === "add-domain" || t.id === "addon-domain") {
      onNav("sites", { toolId: t.id });
      return;
    }
    onNav(t.nav, { toolId: t.id });
  };

  const cpanelToolHref = (t: CpanelTool): string => {
    if (t.section === "ftp") {
      const ftpTab =
        t.hint === "add"
          ? "add"
          : t.hint === "connections"
            ? "connections"
            : "manage";
      return hrefForNav("ftp", { ftpTab, toolId: t.id });
    }
    return hrefForNav(t.nav, { toolId: t.id });
  };

  const toggleSection = (
    secId: WhmSectionId,
    secNav?: NavKey,
    e?: { preventDefault: () => void }
  ) => {
    e?.preventDefault();
    if (secId === "home") {
      setExpanded(null);
      saveExpanded(null);
      setLocalToolId(null);
      onWhmSection?.("all");
      onNav("overview");
      return;
    }
    const next = expanded === secId ? null : secId;
    setExpanded(next);
    saveExpanded(next);
    onWhmSection?.(secId);
    // Opening a section navigates to its hub page
    if (next && secNav) {
      const first = (toolsBySection.get(secId) || [])[0];
      if (first) {
        openTool(first);
      } else {
        onNav(secNav);
      }
    }
  };

  const sectionHref = (secId: WhmSectionId, secNav?: NavKey): string => {
    if (secId === "home" || !secNav) return hrefForAct("home");
    const first = (toolsBySection.get(secId) || [])[0];
    if (first) return toolHref(first);
    return hrefForNav(secNav);
  };

  const isSectionActive = (secId: WhmSectionId, secNav?: NavKey) => {
    if (secId === "home") {
      return (
        nav === "overview" &&
        (!whmSection || whmSection === "all" || whmSection === "home")
      );
    }
    // Prefer active tool's section so Plan vs Package don't both light up on nav=plans
    if (activeToolId) {
      const tool = tools.find((t) => t.id === activeToolId);
      if (tool) return tool.section === secId;
    }
    if (expanded === secId) return true;
    if (secNav && nav === secNav) return true;
    const items = toolsBySection.get(secId) || [];
    return items.some((t) => t.nav === nav);
  };

  const breadcrumb = useMemo(() => {
    if (!whmMode) return null;
    if (nav === "overview" && (!whmSection || whmSection === "all")) {
      return [{ label: "Home" }];
    }
    const sec =
      whmSections.find((s) => s.id === expanded) ||
      whmSections.find((s) => s.nav === nav) ||
      whmSections.find((s) =>
        (toolsBySection.get(s.id) || []).some((t) => t.nav === nav)
      );
    const tool =
      (activeToolId && tools.find((t) => t.id === activeToolId)) ||
      tools.find((t) => t.nav === nav && (!sec || t.section === sec.id));
    const parts: { label: string }[] = [{ label: "Home" }];
    if (sec && sec.id !== "home") parts.push({ label: sec.label });
    if (tool) parts.push({ label: tool.label });
    else if (nav !== "overview") {
      parts.push({
        label: sec?.label || String(nav)
      });
    }
    return parts;
  }, [
    whmMode,
    nav,
    whmSection,
    expanded,
    whmSections,
    toolsBySection,
    activeToolId,
    tools
  ]);

  return (
    <div
      className={`app-shell ${whmMode ? "whm-shell wb-shell" : "cpanel-shell cp-shell"}`}
    >
      <aside className="sidebar">
        <div className="sidebar-brand">
          {branding?.logoUrl ? (
            <img src={branding.logoUrl} alt="" className="wb-brand-logo" />
          ) : (
            <div className="brand-mark wb-brand-mark">
              <svg viewBox="0 0 64 64" fill="none" width="22" height="22">
                <path
                  d="M14 42V22l10 12 10-12v20"
                  stroke="#60A5FA"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle cx="46" cy="24" r="6" stroke="#34D399" strokeWidth="3" />
              </svg>
            </div>
          )}
          <div>
            <h2>{productName}</h2>
            <span>{panelLabel}</span>
          </div>
        </div>

        {whmMode ? (
          <div className="wb-sidebar-search">
            <input
              type="search"
              className="wb-sidebar-search-input"
              placeholder="Search Now (/)"
              value={whmSearch || ""}
              onChange={(e) => {
                onWhmSearch?.(e.target.value);
                if (nav !== "overview") {
                  onWhmSection?.("all");
                  onNav("overview");
                }
              }}
              aria-label="Search WHM tools"
            />
            <span className="wb-sidebar-search-icon" aria-hidden>
              ⌕
            </span>
          </div>
        ) : (
          <div className="wb-sidebar-search">
            <input
              type="search"
              className="wb-sidebar-search-input"
              placeholder="Search"
              value={whmSearch || ""}
              onChange={(e) => {
                onWhmSearch?.(e.target.value);
                if (nav !== "overview") onNav("overview");
              }}
              aria-label="Search cPanel tools"
            />
            <span className="wb-sidebar-search-icon" aria-hidden>
              ⌕
            </span>
          </div>
        )}

        <nav className="wb-nav">
          {whmMode
            ? renderGroupedWhmNav({
                whmSections,
                toolsBySection,
                expanded,
                nav,
                activeToolId,
                isSectionActive,
                toggleSection,
                openTool,
                toolHref,
                sectionHref,
                setExpanded
              })
            : renderCpanelNav({
                userFeatures,
                expanded,
                nav,
                activeToolId,
                setExpanded,
                openCpanelTool,
                cpanelToolHref,
                onNav
              })}
        </nav>

        <div className="sidebar-footer">
          {whmMode ? (
            <div className="wb-sidebar-status" title="Server / license">
              <span
                className={`wb-sidebar-status-dot${
                  licenseStatus && !licenseStatus.valid ? " down" : ""
                }`}
                aria-hidden
              />
              <span>
                {licenseStatus?.serverIp ||
                  (licenseStatus?.valid ? "License active" : "Server ready")}
              </span>
            </div>
          ) : null}
          <div className="user-chip">
            <div className="avatar">
              {user.username.slice(0, 1).toUpperCase()}
            </div>
            <div>
              <strong>{user.username}</strong>
              <small>
                {userPanel ? user.primaryDomain || "cPanel" : user.role}
              </small>
            </div>
          </div>
          <button type="button" className="btn btn-ghost" onClick={onLogout}>
            Sign out
          </button>
        </div>
      </aside>

      <div className="wb-main-col">
        {whmMode ? (
          <header className="wb-topstrip">
            <div className="wb-topstrip-meta">
              <span title="Operating system">{prettyOs}</span>
              {version ? (
                <>
                  <span className="wb-topstrip-sep">·</span>
                  <span title="Panel version">{version}</span>
                </>
              ) : null}
              <span className="wb-topstrip-sep">·</span>
              <span title="Hostname">Hostname: {hostname}</span>
              {loadAvg ? (
                <>
                  <span className="wb-topstrip-sep">·</span>
                  <span title="Load average">Load Average: {loadAvg}</span>
                </>
              ) : null}
            </div>
            <div className="wb-topstrip-actions">
              <span className="wb-logged-pill">
                Logged in as <strong>{user.username}</strong>
              </span>
              <button
                type="button"
                className="wb-icon-btn"
                title="Sign out"
                onClick={onLogout}
              >
                ⏻
              </button>
            </div>
          </header>
        ) : null}

        <main className={`main ${whmMode ? "wb-main" : ""}`}>
          {whmMode && breadcrumb && breadcrumb.length > 1 ? (
            <nav className="wb-breadcrumb-bar" aria-label="Breadcrumb">
              {breadcrumb.map((p, i) => (
                <span key={`${p.label}-${i}`} className="wb-bc-part">
                  {i > 0 ? (
                    <span className="wb-bc-sep" aria-hidden>
                      ›
                    </span>
                  ) : null}
                  <span
                    className={
                      i === breadcrumb.length - 1
                        ? "wb-bc-current"
                        : "wb-bc-link"
                    }
                  >
                    {p.label}
                  </span>
                </span>
              ))}
            </nav>
          ) : null}

          {session?.impersonating ? (
            <div className="impersonation-banner">
              <div>
                <strong>cPanel</strong> as{" "}
                <span className="mono">{user.username}</span>
                <span className="muted">
                  {" "}
                  · logged in from WHM (
                  {session.impersonatorUsername || "admin"})
                </span>
              </div>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={onStopImpersonation}
              >
                Exit to WHM
              </button>
            </div>
          ) : null}
          {/* Large license banner removed — compact license lives on Overview */}
          {licenseStatus &&
          (staff || reseller) &&
          !session?.impersonating &&
          !licenseStatus.valid ? (
            <div className="license-banner license-banner-crit">
              <div>
                <strong>License expired / blocked</strong>
                <span className="muted">
                  {" "}
                  · {licenseStatus.message || licenseStatus.status}
                </span>
              </div>
              {onOpenLicenses ? (
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={onOpenLicenses}
                >
                  View License
                </button>
              ) : null}
            </div>
          ) : null}
          {children}
        </main>
      </div>
    </div>
  );
}

/** cPanel left nav — every section is a real `?act=` link + expandable tools */
function renderCpanelNav(opts: {
  userFeatures?: Record<string, boolean> | null;
  expanded: string | null;
  nav: NavKey;
  activeToolId: string | null;
  setExpanded: (id: string | null) => void;
  openCpanelTool: (
    t: CpanelTool,
    e?: { preventDefault: () => void }
  ) => void;
  cpanelToolHref: (t: CpanelTool) => string;
  onNav: (k: NavKey, opts?: WhmNavOpts) => void;
}): ReactNode {
  const {
    userFeatures,
    expanded,
    nav,
    activeToolId,
    setExpanded,
    openCpanelTool,
    cpanelToolHref,
    onNav
  } = opts;

  const sections = CPANEL_SECTIONS.filter((sec) => {
    if (sec.id === "home") return true;
    if (sec.id === "ssl") return userFeatures?.sites !== false;
    if (sec.id === "ftp" || sec.id === "files")
      return userFeatures?.files !== false;
    if (sec.id === "software") return userFeatures?.apps !== false;
    if (sec.id === "security") return userFeatures?.security !== false;
    if (sec.id === "mail") return userFeatures?.email !== false;
    if (sec.id === "databases") return userFeatures?.databases !== false;
    if (sec.id === "domains") return userFeatures?.sites !== false;
    if (sec.id === "advanced") return userFeatures?.cron !== false;
    if (sec.id === "metrics") return userFeatures?.logs !== false;
    return true;
  });

  return sections.map((sec) => {
    const secId = sec.id as CpanelSectionId;
    const tools = cpanelSectionTools(secId, userFeatures);
    const hasChildren = secId !== "home" && tools.length > 0;
    const open = expanded === secId && hasChildren;
    const secNav = sec.nav || "overview";
    const active =
      secId === "home"
        ? nav === "overview"
        : nav === secNav || tools.some((t) => t.id === activeToolId);

    // Home — single link
    if (secId === "home") {
      return (
        <a
          key="home"
          href={hrefForAct("home")}
          className={`nav-item wb-nav-item ${nav === "overview" ? "active" : ""}`}
          onClick={(e) => {
            e.preventDefault();
            setExpanded(null);
            saveExpanded(null);
            onNav("overview");
          }}
        >
          <span className="wb-nav-icon" aria-hidden>
            {CPANEL_SECTION_ICONS.home}
          </span>
          <span className="wb-nav-label">Home</span>
        </a>
      );
    }

    const headerAct = cpanelSectionDefaultAct(secId);
    const headerHref = hrefForAct(headerAct);
    const icon =
      CPANEL_SECTION_ICONS[secId] ||
      (secId === "software" ? "✦" : secId === "security" ? "⛨" : "•");

    return (
      <div
        key={secId}
        className={`wb-nav-group ${open ? "open" : ""} ${active ? "active-group" : ""}`}
      >
        <a
          href={headerHref}
          className={`nav-item wb-nav-item ${active && !open ? "active" : ""} ${open ? "expanded" : ""}`}
          aria-expanded={hasChildren ? open : undefined}
          onClick={(e) => {
            e.preventDefault();
            const next = open ? null : secId;
            setExpanded(next);
            saveExpanded(next);
            // Navigate to section hub + first tool if any
            if (next) {
              const first = tools[0];
              if (first) openCpanelTool(first);
              else onNav(secNav);
            } else {
              // collapsing still lands on section page
              onNav(secNav, tools[0] ? { toolId: tools[0].id } : undefined);
            }
          }}
        >
          <span className="wb-nav-icon" aria-hidden>
            {icon}
          </span>
          <span className="wb-nav-label">{sec.label}</span>
          {hasChildren ? (
            <span
              className={`wb-nav-chevron ${open ? "open" : ""}`}
              aria-hidden
            >
              ›
            </span>
          ) : null}
        </a>
        {hasChildren && open ? (
          <div className="wb-submenu" role="group">
            {tools.map((t) => (
              <a
                key={t.id}
                href={cpanelToolHref(t)}
                className={`wb-submenu-item ${
                  activeToolId === t.id ||
                  (activeToolId == null && t.nav === nav && t.id === tools[0]?.id)
                    ? "active"
                    : ""
                }`}
                onClick={(e) => openCpanelTool(t, e)}
              >
                <span className="wb-submenu-dash">–</span>
                <span>{t.label}</span>
              </a>
            ))}
          </div>
        ) : null}
      </div>
    );
  });
}

function renderGroupedWhmNav(opts: {
  whmSections: { id: WhmSectionId; label: string; nav?: NavKey }[];
  toolsBySection: Map<string, WhmTool[]>;
  expanded: string | null;
  nav: NavKey;
  activeToolId: string | null;
  isSectionActive: (secId: WhmSectionId, secNav?: NavKey) => boolean;
  toggleSection: (
    secId: WhmSectionId,
    secNav?: NavKey,
    e?: { preventDefault: () => void }
  ) => void;
  openTool: (t: WhmTool, e?: { preventDefault: () => void }) => void;
  toolHref: (t: WhmTool) => string;
  sectionHref: (secId: WhmSectionId, secNav?: NavKey) => string;
  setExpanded: (id: string | null) => void;
}): ReactNode {
  const {
    whmSections,
    toolsBySection,
    expanded,
    nav,
    activeToolId,
    isSectionActive,
    toggleSection,
    openTool,
    toolHref,
    sectionHref,
    setExpanded
  } = opts;

  const groups: { label: string; ids: WhmSectionId[] }[] = [
    {
      label: "Management",
      ids: ["home", "accounts", "plan", "domains"]
    },
    { label: "Services", ids: ["dns", "email", "sql", "ssl"] },
    {
      label: "Security & Reliability",
      ids: ["security", "backup", "import", "transfers"]
    },
    {
      label: "System",
      ids: ["server", "apps", "software", "advanced"]
    },
    /**
     * Package at the bottom of the menu (owner packages, VPS/reseller plans,
     * reseller center). Resellers only see Branding here.
     */
    { label: "Package", ids: ["packages"] }
  ];

  const rendered = new Set<string>();
  const blocks: ReactNode[] = [];

  const renderSec = (sec: (typeof whmSections)[0]) => {
    const items = toolsBySection.get(sec.id) || [];
    const hasChildren = sec.id !== "home" && items.length > 0;
    const open = expanded === sec.id && hasChildren;
    const active = isSectionActive(sec.id, sec.nav);
    rendered.add(sec.id);
    return (
      <div
        key={sec.id}
        className={`wb-nav-group ${open ? "open" : ""} ${active ? "active-group" : ""}`}
      >
        <a
          href={sectionHref(sec.id, sec.nav)}
          className={`nav-item wb-nav-item ${active && !open ? "active" : ""} ${open ? "expanded" : ""}`}
          onClick={(e) => toggleSection(sec.id, sec.nav, e)}
          aria-expanded={hasChildren ? open : undefined}
        >
          <span className="wb-nav-icon" aria-hidden>
            {SECTION_ICONS[sec.id] || "•"}
          </span>
          <span className="wb-nav-label">{sec.label}</span>
          {hasChildren ? (
            <span
              className={`wb-nav-chevron ${open ? "open" : ""}`}
              aria-hidden
            >
              ›
            </span>
          ) : null}
        </a>
        {hasChildren && open ? (
          <div className="wb-submenu" role="group">
            {items.map((t) => {
              const isActive =
                activeToolId === t.id ||
                (activeToolId == null &&
                  t.nav === nav &&
                  t.id ===
                    (items.find((x) => x.nav === nav)?.id || items[0]?.id));
              return (
                <a
                  key={t.id}
                  href={toolHref(t)}
                  className={`wb-submenu-item ${isActive ? "active" : ""}`}
                  onClick={(e) => {
                    setExpanded(sec.id);
                    saveExpanded(sec.id);
                    openTool(t, e);
                  }}
                >
                  <span className="wb-submenu-dash">–</span>
                  <span>{t.label}</span>
                </a>
              );
            })}
          </div>
        ) : null}
      </div>
    );
  };

  for (const g of groups) {
    const secs = whmSections.filter((s) => g.ids.includes(s.id));
    if (!secs.length) continue;
    blocks.push(
      <div key={g.label} className="wb-nav-block">
        <div className="wb-sidebar-label">{g.label}</div>
        {secs.map(renderSec)}
      </div>
    );
  }
  const rest = whmSections.filter((s) => !rendered.has(s.id));
  if (rest.length) {
    blocks.push(
      <div key="more" className="wb-nav-block">
        <div className="wb-sidebar-label">More</div>
        {rest.map(renderSec)}
      </div>
    );
  }
  return blocks;
}
